import React, { useEffect, useMemo, useState } from "react"
import { Box, Grid } from "@mui/material"
import { useDispatch, useSelector } from "react-redux"
import { Formik } from "formik"

import Header from "components/Header/Header"
import AppButton from "components/StyledComponents/AppButton"
import UsageMatricsTable from "./UsageMatricsTable"
import NotifyForm from "./NotifyForm"

import { createBreadcrumbs } from "helpers/breadCrumbs"
import { validationSchemaNotify } from "helpers/validationSchema"
import { convertDate, payloadDate } from "helpers/functions"
import { appActions } from "redux/app/slice/app"
import {
  exportUsageMetricsAction,
  getUsageMeticsAction,
  getUserRolesAction,
  notifyUserAction
} from "redux/DMV/actions/user"
import { useDebounce } from "hooks/utils"
import UserCategoryCard from "./UserCategoryFilter"

const userCategory = [
  { label: "Active Users", value: "active_users" },
  { label: "AWOL Users", value: "awol_users" }
]

const DEFAULT_NOTIFICATION_VALUES = Object.freeze({
  notificationName: "",
  uploadThumbnail: "",
  scheduleNotificationDate: "",
  scheduleNotificationTime: "",
  notificationContent: "",
  startDate: "",
  endDate: "",
  select_all: "",
  restaurant: "",
  user_ids: ""
})

const UsageMetrics = () => {
  const dispatch = useDispatch()

  const {
    userType = "",
    restaurant,
    orderBy,
    searchField,
    raffleAccess,
    employeeClass,
    startDate,
    endDate,
    userStatus,
    usersLoginStatus
  } = useSelector((state) => state?.app?.filters || {})
  const usageMetricsList = useSelector((state) => state?.dmv?.usageMetricsList || [])
  const { notificationImageId } = useSelector((store) => store?.superAdmin?.notifications || [])
  const isAwol = useMemo(() => userType === "awol_users", [userType])
  const debouncedSearchTerm = useDebounce(searchField, 500)
  const [currentPage, setCurrentPage] = useState(1)
  const [openModal, setOpenModal] = useState(false)
  const [initialValues, setInitialValues] = useState(DEFAULT_NOTIFICATION_VALUES)
  const [selectedUserIds, setSelectedUserIds] = useState([])

  const itemsPerPage = 12
  const totalPages = Math.ceil(usageMetricsList?.length / itemsPerPage)
  const paginatedList = useMemo(
    () => usageMetricsList?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [usageMetricsList, currentPage]
  )

  useEffect(() => {
    document.title = "Usage Metrics"
    dispatch(appActions.initFilters())
    dispatch(appActions.setOrderBy("streak"))
    dispatch(getUsageMeticsAction({}))
    dispatch(getUserRolesAction({}))
  }, [])

  useEffect(() => {
    setCurrentPage(1)
    dispatch(
      getUsageMeticsAction({
        userType,
        restaurant,
        orderBy,
        search: debouncedSearchTerm,
        raffleAccess,
        employeeClass,
        startDate,
        endDate,
        userStatus,
        usersLoginStatus
      })
    )
  }, [
    userType,
    restaurant,
    orderBy,
    debouncedSearchTerm,
    raffleAccess,
    employeeClass,
    startDate,
    endDate,
    userStatus,
    usersLoginStatus
  ])

  useEffect(() => {
    dispatch(getUserRolesAction({ restaurant: restaurant }))
    const payload = []
    payload["employeeClass"] = "ALL"
    dispatch(appActions.setFilters(payload))
  }, [restaurant])

  useEffect(() => {
    setSelectedUserIds([])
  }, [userType, restaurant])

  const handleCheckboxChange = (userId) => {
    setSelectedUserIds((prev) =>
      prev?.includes(userId) ? prev?.filter((id) => id !== userId) : [...prev, userId]
    )
  }

  const handleSelectAllChange = (shouldSelectAll) => {
    const awolUserIds = shouldSelectAll ? usageMetricsList?.map((row) => row?.user_id) : []
    setSelectedUserIds(awolUserIds)
  }

  const handleNotifyAll = (row = {}) => {
    setInitialValues({
      ...DEFAULT_NOTIFICATION_VALUES,
      select_all: row?.name ? false : true,
      restaurant: row?.restaurant_id || "",
      user_ids: row?.user_id || ""
    })
    setOpenModal(true)
  }

  const handleExport = () => {
    dispatch(
      exportUsageMetricsAction({
        userType,
        restaurant,
        orderBy,
        raffleAccess,
        employeeClass,
        startDate,
        endDate
      })
    )
  }

  const handleClose = () => {
    setOpenModal(false)
    setSelectedUserIds([])
    setInitialValues(DEFAULT_NOTIFICATION_VALUES)
  }

  const handleSubmit = (values) => {
    const NotificationTimeStamp = convertDate(
      values.scheduleNotificationDate,
      values.scheduleNotificationTime
    )

    const NotificationTimeSplit = NotificationTimeStamp.split(" ")
    const payloadScheduledDate = payloadDate(NotificationTimeSplit)

    const payload = {
      title: values.notificationName,
      scheduled_date: payloadScheduledDate || "",
      scheduled_time: NotificationTimeSplit[4] || "",
      is_scheduled: true,
      select_all: values.select_all,
      year: new Date().getFullYear(),
      ...(values?.restaurant && { restaurant: values.restaurant }),
      user_ids: selectedUserIds ? selectedUserIds : values.user_ids ? [values.user_ids] : [],
      content: values.notificationContent || "",
      status: "scheduled",
      ...(notificationImageId
        ? { image: notificationImageId }
        : values.uploadThumbnail
        ? {}
        : { image: "" })
    }

    dispatch(notifyUserAction({ payload, handleClose }))
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Header title="Usage Metrics" breadCrumbs={createBreadcrumbs()}>
          <Grid container item gap={2}>
            <AppButton
              sx={{ padding: "13px 36px" }}
              disabled={paginatedList?.length === 0}
              onClick={handleExport}>
              Export
            </AppButton>
            {isAwol && (
              <AppButton
                sx={{ padding: "13px 36px" }}
                disabled={paginatedList?.length === 0}
                onClick={() => handleNotifyAll()}>
                Notify All
              </AppButton>
            )}
          </Grid>
        </Header>
      </Grid>
      <Box className="d-flex w-100 flex-wrap m-2">
        {userCategory?.map((option, index) => (
          <UserCategoryCard
            key={index}
            title={option?.label}
            selectedOption={option?.value === userType}
            onSelect={() => dispatch(appActions.setUserType(option?.value))}
          />
        ))}
      </Box>
      <Grid item xs={12}>
        <UsageMatricsTable
          currentPage={currentPage}
          totalPage={totalPages}
          rowsData={paginatedList}
          columns={columns}
          isAwol={isAwol}
          handleNotifyAll={handleNotifyAll}
          setCurrentPage={setCurrentPage}
          selectedUserIds={selectedUserIds}
          handleCheckboxChange={handleCheckboxChange}
          handleSelectAllChange={handleSelectAllChange}
          usageMetricsList={usageMetricsList}
        />
      </Grid>

      {openModal && (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchemaNotify}
          onSubmit={handleSubmit}>
          {(formik) => <NotifyForm onClose={handleClose} formik={formik} open={openModal} />}
        </Formik>
      )}
    </Grid>
  )
}

export default UsageMetrics

const columns = [
  { id: "checkbox", label: "checkbox", align: "center", minWidth: 48, isHidden: true },
  { id: "id", label: "ID", align: "center", minWidth: 60 },
  {
    id: "streak",
    label: "Streaks",
    align: "center",
    minWidth: 120,
    isActive: true,
    isSorting: true
  },
  { id: "name", label: "Name", minWidth: 200 },
  { id: "role", label: "Role", align: "center", minWidth: 100 },
  { id: "restaurant", label: "Restaurant", align: "center", minWidth: 140 },
  {
    id: "longest_streak",
    label: "Longest streak",
    align: "center",
    minWidth: 170,
    isHidden: true,
    isSorting: true
  },
  {
    id: "last_login",
    label: "Last Login(Last Played)",
    align: "center",
    minWidth: 120,
    isHidden: true
  },
  {
    id: "days_awol",
    label: "Days AWOL",
    align: "center",
    minWidth: 140,
    isHidden: true,
    isSorting: true
  },
  {
    id: "trivia_games",
    label: "Trivia Games Played",
    align: "center",
    minWidth: 195,
    isSorting: true
  },
  { id: "quiz_games", label: "Quiz Games Played", align: "center", minWidth: 190, isSorting: true },
  {
    id: "vip_quiz_count",
    label: "VIP Quiz Games Played",
    align: "center",
    minWidth: 215,
    isSorting: true
  },
  {
    id: "weekly_challenge_count",
    label: "Weekly Challenges Played",
    align: "center",
    minWidth: 210
  },
  {
    id: "history_challenge_count",
    label: "History Challenges Played",
    align: "center",
    minWidth: 210
  },
  {
    id: "daily_practice_count",
    label: "Random Questions Played",
    align: "center",
    minWidth: 200
  },
  {
    id: "daily_practice_percentage",
    label: "Random Questions Correct %",
    align: "center",
    minWidth: 200
  },
  {
    id: "wine_region_count",
    label: "Wine Region Played",
    align: "center",
    minWidth: 200
  },
  {
    id: "wine_region_percentage",
    label: "Wine Region Correct %",
    align: "center",
    minWidth: 200
  },
  {
    id: "mistake_attempts",
    label: "Mistake Attempts",
    align: "center",
    minWidth: 200
  },
  {
    id: "tasting_notes_count",
    label: "Tasting Notes Uploaded",
    align: "center",
    minWidth: 220,
    isSorting: true
  },
  {
    id: "feature_item_upload",
    label: "Featured Item Uploaded",
    align: "center",
    minWidth: 220,
    isSorting: true
  },
  {
    id: "trivia_percentage",
    label: "Trivia Correct %",
    align: "center",
    minWidth: 165,
    isSorting: true
  },
  {
    id: "quiz_percentage",
    label: "Quiz Correct %",
    align: "center",
    minWidth: 165,
    isSorting: true
  },
  { id: "action", label: "Action", align: "center", minWidth: 125, isHidden: true }
]
